import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Skeleton } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';

import type { PlayableParams, PlayableTemplate } from '../../playable-api';
import { buildPlayablePreviewDoc, type PlayableRuntime } from '../../playable-preview';
import { TemplatePicker, type PreviewState } from '../PlayableComposer/TemplatePicker';
import * as S from './Html5BannerLibrary.styles';

export interface StyleStepProps {
  /** The styles that can advertise the chosen subject, in the registry's order (empty while the registry loads). */
  styles: PlayableTemplate[];
  /** The built runtime (the cards play live); null when this environment has none. */
  runtime: PlayableRuntime | null;
  /** The registry has been read (its styles are known). */
  loaded: boolean;
  /** The registry could not be read. */
  failed: boolean;
  /** The new banner's document as it would start (the subject, its copy and the brand theme); null while the
   *  brand theme is read. */
  doc: PlayableParams | null;
  /** The draft is being created. */
  busy: boolean;
  onPick: (style: PlayableTemplate) => void;
  onBack: () => void;
  onClose: () => void;
}

/** The size the style cards play in (the medium rectangle, every style's default). */
const CARD_FORMAT = '300x250';
/** A style card's preview box. */
const CARD_W = 240;
const CARD_H = 200;

/**
 * A new live-text banner's style: one card per style that fits its subject, each playing the banner live with the
 * subject's own copy and the brand's colours (no art yet: the styles' palette fallbacks). Picking one creates the
 * banner and opens its composer, where the style can still be changed.
 */
export const StyleStep = ({
  styles,
  runtime,
  loaded,
  failed,
  doc,
  busy,
  onPick,
  onBack,
  onClose,
}: StyleStepProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle } = useSheetDismiss(onClose);
  const previews = React.useMemo<Record<string, PreviewState>>(() => {
    const out: Record<string, PreviewState> = {};
    if (!doc || !runtime) return out;
    styles.forEach((style) => {
      // A controlled preview: it rests on the final frame until the card plays the frames through.
      const html = buildPlayablePreviewDoc(
        runtime,
        style.id,
        CARD_FORMAT,
        { ...doc, template_id: style.id, schema_version: style.schema_version },
        undefined,
        { preview: true },
      );
      out[style.id] = html ? { status: 'ready', html } : { status: 'unavailable' };
    });
    return out;
  }, [doc, runtime, styles]);

  return (
    <Sheet
      title={t('Choose a style')}
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="1100px"
      footer={
        <S.FootActions>
          <Button variant="text" label={t('Back')} onClick={onBack} disabled={busy} />
        </S.FootActions>
      }
    >
      <S.StepBody>
        <S.Hint text="How the banner moves. Every style shows the copy of what you advertise as live text. The style stays once it is chosen." />
        {styles.length > 0 && doc ? (
          <TemplatePicker
            templates={styles}
            selectedId=""
            previews={previews}
            canPreview={runtime !== null}
            previewFormat={CARD_FORMAT}
            disabled={busy}
            cardW={CARD_W}
            cardH={CARD_H}
            ariaLabel={t('Style')}
            onPick={(id) => {
              const style = styles.find((s) => s.id === id);
              if (style && !busy) onPick(style);
            }}
          />
        ) : failed ? (
          <S.Hint text="The styles couldn't be loaded. Close this and try again." />
        ) : loaded && styles.length === 0 ? (
          <S.Hint text="No style is available for this subject yet." />
        ) : (
          <Skeleton block height={260} radius={12} />
        )}
        {busy ? <S.MintingText text="Setting up your HTML5 banner…" /> : null}
      </S.StepBody>
    </Sheet>
  );
};

export default StyleStep;
